import React, { useContext, useEffect, useRef, useState } from "react";
import { IAddQuotationProps } from "./AddQuotation.types";
import { ICustomerAddress } from "../Common/Interfaces/ICustomerAddress";
import {
  ComboBox,
  DatePicker,
  FontSizes,
  FontWeights,
  IComboBox,
  IComboBoxOption,
  IDatePicker,
  IDropdownOption,
  Label,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  StackItem,
  TextField,
  defaultDatePickerStrings,
  IBreadcrumbItem,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import { useDateFormatter } from "../../../Utils/DateFormatter";
import {
  Breadcrumb,
  Card,
  CardFooterBase,
  LoadingState,
} from "@coherence-design-system/controls";
import {
  stackTokensWithGap20,
  stackTokensWithGap30,
  stackTokensWithGap40,
} from "../QuotationStyle";
import { QuotationContext, expirationDate } from "../QuotationContext";
import {
  IQuotationType,
  IResponseMessage,
  QuotationInfoType,
} from "../Quotation.types";
import {
  BILL_TO,
  DefaultCustomerDiscounts,
  DefaultEssentialBundleDiscount,
  DeliveryOptions,
  DeliveryReasons,
  EDIT_ACTION,
  InitialQuotationInfo,
  InitialStateOfResponseMessage,
  NEW_ACTION,
  NO,
  PaymentMethods,
  PaymentOptions,
  PaymentTerms,
  SHIP_TO,
  YES,
} from "../Common/QuotationData";
import { appInsights } from "../../../services/Logging/AppInsights";
import { QuotationDetailService } from "../../../services/Quotations/QuotationDetailService";
import { QuoteSessionTracker } from "../../../Utils/QuoteSessionTracker";
import { CustomerService } from "../../../services/Customers/CustomerService";
import { useLocation, useNavigate } from "react-router-dom";
import {
  onQuotationSave,
  resetQuotationForm,
  validateMsxOppertunityId,
  validateQuotationHeaderFields,
} from "../Common/Utilities/Quotation";
import {
  BreadcrumbStyle,
  GrayText,
  LoadingContainerStyle,
  MarginBlockEndStyle,
  btn_width,
  cardWidth850,
  comboBoxStyles,
  customerAccountDetailsstyle,
  datePickerStyles,
  errorDatePickerStyles,
  textFieldGrayStyles,
  textFieldStyles,
} from "./AddQuotationStyle";
import { AddressesAndContactsComponent } from "../Components/AddressesAndContactsComponent/AddressesAndContactsComponent";
import { getCountriesData } from "../../../Utils/CountriesData";
import { useLocalStorageContext } from "../../../Provider/LocalStorageProvider";
import { fetchLoggedInUser } from "../../../Utils/FetchLoggedInUser";
import { IUserInfo } from "../../../common/Interfaces/IUserInfo";
import { useId } from "@fluentui/react-hooks";
import {
  allowSpecialCharacterExcludingSome,
  CUSTOMER_PURCHASE_ORDER_VALIDATION,
  EMAIL_VALIDATION,
  emailRegex,
  msxOpportunityIdRegex,
  MSX_OPPORTUNITY_ID_VALIDATION,
  phoneNumberRegex,
  poNumberRegex,
  SOME_PHONE_SPECIAL_CHARACTERS_NOT_ALLOWED,
  SOME_SPECIAL_CHARACTERS_NOT_ALLOWED,
} from "../../../Utils/Common";
import {
  EMAIL_CHARACTER_LIMIT,
  INVALID_PHONENUMBER,
  SPECIL_CHARACTER_INVALID_PHONENUMBER,
  contactNumberRegex,
  emailCharacterLimit,
} from "../../CustomerAccount/AddNewCustomerAccount/AddCustomerValidation";
import { addDaysToDate } from "../../../Utils/Date";

const CustomCardHeader = ({ cardTitle }: { cardTitle: string }) => {
  return (
    <Stack aria-label={cardTitle} aria-labelledby={cardTitle} tabIndex={-1}>
      <h2
        style={{ fontWeight: "600", fontSize: "14px", padding: "0 4px 0 16px" }}
      >
        {cardTitle}
      </h2>
    </Stack>
  );
};

const CustomLabel: React.FC<{ text: string }> = ({ text }) => {
  const labelId = useId("expirationDateLabel");
  return (
    <Label
      htmlFor={labelId}
      required
      aria-label={`Quotation Details ${text}`}
      id={labelId}
    >
      {text}
    </Label>
  );
};

export const AddQuotation: React.FunctionComponent<IAddQuotationProps> = (
  props
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { countries, setUser } = useLocalStorageContext();
  const { quotationInfo, setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
    // --- Date formatting for country ---
    const getCountryName = () => {
      const address = quotationInfo.customer?.selectedShipToAddress;
      if (!address) return undefined;
      return address.country;
    };
    const { formatDate } = useDateFormatter(getCountryName());
    // Wrapper to match DatePicker's expected signature
    const formatDateForPicker = (date?: Date | undefined) => {
      if (!date || isNaN(date.getTime())) return "";
      return formatDate(date);
    };
  const { quotationError, setQuotationError } = useContext(
    QuotationContext
  ) as IQuotationType;
  const datePickerRef = useRef<IDatePicker>(null);
  const [quotationSuccessMessage, setQuotationSuccessMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationFailureMessage, setQuotationFailureMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationWarningMessage, setQuotationWarningMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationWaitMessage, setQuotationWaitMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<IUserInfo>({
    id: "",
    displayName: "",
  });

  const [taxExpireAlertMessage, setTaxExpireAlertMessage] = useState("");
  useEffect(() => {
    if (userInfo && userInfo.id) {
      if (
        location?.state?.actionName === NEW_ACTION &&
        (quotationInfo.createdBy === "" || quotationInfo.createdByName === "")
      ) {
        setQuotationInfo({
          ...quotationInfo,
          createdBy: userInfo.id,
          createdByName: userInfo.displayName,
        });
      } else if (
        location?.state?.actionName === EDIT_ACTION &&
        (quotationInfo.modifiedById === "" ||
          quotationInfo.modifiedByName === "")
      ) {
        setQuotationInfo({
          ...quotationInfo,
          modifiedById: userInfo.id,
          modifiedByName: userInfo.displayName,
        });
      }
    }
  }, [userInfo]);

  const today = new Date();
  useEffect(() => {    
    // Start tracking the quote session when component mounts
    QuoteSessionTracker.startSession();
    
    if (
      quotationInfo.quotationNumber === "" ||
      quotationInfo.quotationNumber === undefined
    ) {
      setQuotationInfo({
        ...quotationInfo,
        quotationDate: today.toUTCString(),
        quotationNumber: quotationInfo.quotationNumber
          ? quotationInfo.quotationNumber
          : "",
      });
    }
  }, []);

  const getBreadcrumbItems = () => {
    const items: IBreadcrumbItem[] = [
      {
        text: "Quotations",
        key: "Quotations",
        onClick: () => {
          navigate("/AllQuotations");
        },
      },
      {
        text: `${location?.state?.actionName} Quotation`,
        key: `${location?.state?.actionName} Quotation`,
      },
    ];
    if (
      location?.state?.actionName === EDIT_ACTION &&
      quotationInfo?.quotationNumber
    ) {
      items.push({
        text: quotationNumber,
        key: quotationNumber,
        isCurrentItem: true,
      });
    } else if (
      location?.state?.actionName !== EDIT_ACTION &&
      quotationInfo?.customer?.name
    ) {
      items.push({
        text: name,
        key: name,
        isCurrentItem: true,
      });
    }
    return items;
  };
  const checkTaxExpiration = (expiryDate: Date, taxExpirationDate: Date) => {
    const taxExpirationDatePlus30 = addDaysToDate(taxExpirationDate, 30);
    const isExpiryBeforeTaxPlus30 = expiryDate > taxExpirationDatePlus30;
    if (isExpiryBeforeTaxPlus30) {
      setTaxExpireAlertMessage(
       "Alert: Tax Exempt Date expires before quote expiration date entered."
      );
    } else {
      setTaxExpireAlertMessage("");
    }    
  };
  useEffect(() => {
    props.onQuotation(true);
    if (location?.state?.actionName === NEW_ACTION) {
      fetchLoggedInUser(setUserInfo);
    }
    if (!location?.state?.quotationId && quotationInfo.quotationNumber === "") {
      intializeQuotation();
    }
    if (location?.state?.actionName) {
      setPageLoading(true);
      if (
        (!quotationWarningMessage.showMessage &&
          quotationInfo?.standaloneItems &&
          quotationInfo?.standaloneItemsClone &&
          JSON.stringify(quotationInfo?.standaloneItems) !==
            JSON.stringify(quotationInfo?.standaloneItemsClone)) ||
        (quotationInfo?.bundleItems &&
          quotationInfo?.bundleItemsClone &&
          JSON.stringify(quotationInfo?.bundleItems) !==
            JSON.stringify(quotationInfo?.bundleItemsClone))
      ) {
        setQuotationWarningMessage({
          showMessage: true,
          message:
            location?.state?.actionName === NEW_ACTION
              ? `You have some unsaved data in your cart, please click on Create to proceed.`
              : `You have some unsaved data in your cart, please click on Update to proceed.`,
        });
      } else {
        setQuotationWarningMessage({
          ...quotationWarningMessage,
          showMessage: false,
        });
      }
    }
  }, []);
  useEffect(() => {
    if (
      location?.state?.actionName === EDIT_ACTION &&
      location?.state?.quotationId &&
      (quotationInfo.selectedPaymentType === undefined ||
        (quotationInfo.selectedPaymentType &&
          Object.keys(quotationInfo.selectedPaymentType).length === 0 &&
          quotationInfo.selectedPaymentType.constructor === Object))
    ) {
      setIsLoading(true);
      QuotationDetailService.getQuotationDetail({
        searchText: location?.state?.quotationId,
        partitionKey: location?.state?.partitionKey,
      })
        .then((res) => {
          appInsights.trackEvent({
            name: "In AddQuotation.tsx, quote details fetched successfully.",
            properties: { quotationId: res.data.data.quotationId },
          });
          const { data } = res.data;
          const desiredPaymentTypeOption = PaymentOptions.find(
            (option: IDropdownOption) => option.key === data.paymentType
          );
          const desiredPaymentMethodOption = PaymentMethods.find(
            (option: IDropdownOption) => option.key === data.paymentMethod
          );
          const desiredPaymentTermsOption = PaymentTerms.find(
            (option: IDropdownOption) => option.key === data.paymentTerms
          );
          const desiredDeliveryModeOption = DeliveryOptions.find(
            (option: IDropdownOption) => option.key === data.deliveryMode
          );
          const desiredDeliveryReasonOption = DeliveryReasons.find(
            (option: IDropdownOption) => option.key === data.deliveryReason
          );
          setQuotationInfo({
            ...quotationInfo,
            ...data,
            taxExpirationDate:data?.taxExpirationDate,
            createdByName: data.createdByName,
            expiryDate: data.expiryDate,
            selectedPaymentType:
              desiredPaymentTypeOption ?? quotationInfo.selectedPaymentType,
            selectedPaymentMethod:
              desiredPaymentMethodOption ?? quotationInfo.selectedPaymentMethod,
            selectedPaymentTerms:
              desiredPaymentTermsOption ?? quotationInfo.selectedPaymentTerms,
            selectedDeliveryMode:
              desiredDeliveryModeOption ?? quotationInfo.selectedDeliveryMode,
            selectedDeliveryReason:
              desiredDeliveryReasonOption ??
              quotationInfo.selectedDeliveryReason,
            quoteLineItems: quotationInfo?.quoteLineItems?.length
              ? quotationInfo.quoteLineItems
              : data.itemDetail ?? [],
            quoteLineItemsClone:
              quotationInfo?.quoteLineItemsClone?.length > 0
                ? quotationInfo.quoteLineItemsClone
                : data.itemDetail,
            bundles: quotationInfo?.bundles?.length
              ? quotationInfo.bundles
              : data.bundles ?? [],
            bundleItems: quotationInfo?.bundleItems?.length
              ? quotationInfo.bundleItems
              : data.bundleItems ?? [],
            bundleItemsClone: quotationInfo?.bundleItems?.length
              ? quotationInfo.bundleItems
              : data.bundleItems ?? [],
            standaloneItems: quotationInfo?.standaloneItems?.length
              ? quotationInfo.standaloneItems
              : data.standaloneItems ?? [],
            standaloneItemsClone: quotationInfo?.standaloneItems?.length
              ? quotationInfo.standaloneItems
              : data.standaloneItems ?? [],
          });
          if(data?.expiryDate && data?.expirationDate){
            checkTaxExpiration(new Date(data?.expiryDate),new Date(data?.expirationDate));
          } 
          setIsLoading(false);
          setPageLoading(false);
        })
        .catch((error) => {
          appInsights.trackException({
            error,
            properties: {
              name: "In AddQuotation.tsx, error while getting searched quotation details.",
            },
          });
          setIsLoading(false);
          setPageLoading(false);
        });
    } else if (
      location?.state?.actionName !== EDIT_ACTION &&
      ((location?.state?.customerId &&
        quotationInfo?.customer?.accountId !== location?.state?.customerId) ||
        location?.state?.accountId)
    ) {
      setPageLoading(true);
      appInsights.trackEvent({
        name: "In AddQuotation.tsx, calling get customer by account Id API on customer selection.",
        properties: { quotationNumber: quotationInfo.quotationNumber },
      });
      setIsLoading(true);
      CustomerService.getCustomerByAccountId(
        location?.state?.customerId || location?.state?.accountId
      )
        .then((res) => {
          if (res.data.code === 200) {
            if (location?.state?.actionName !== EDIT_ACTION) {
              intializeQuotation();
            }
            const { data } = res.data;
            const {
              billTo,
              shipTo,
              selectedBillToAddress,
              selectedShipToAddress,
              selectedBillToContact,
              selectedShipToContact,
              customerDiscount,
              paymentTerms,
            } = res.data.data;
            const customerCountry = getCountry(
              selectedShipToAddress ? selectedShipToAddress : shipTo[0]
            );
            appInsights.trackEvent({
              name: "In AddQuotation.tsx, Customer data has been obtained successfully.",
              properties: { customerName: res.data.data.name },
            });

            const primaryBillToAddress = billTo.find(
              (address: { isAddressPrimary: string }) =>
                address.isAddressPrimary === YES
            );
            const primaryShipToAddress = shipTo.find(
              (address: { isAddressPrimary: string }) =>
                address.isAddressPrimary === YES
            );
            const selectedPaymentTerms = PaymentTerms.find(
              (term: { key: string }) => term.key === paymentTerms
            );
            
            // Set default Payment Type and Payment Method based on customer's Payment Terms
            const isPrePaidPayment = (paymentTerms === "CreditCard" || paymentTerms === "WirePrepay");
            const defaultPaymentType = isPrePaidPayment 
              ? PaymentOptions[0] // PrePaid
              : PaymentOptions[1]; // PostPaid
            
            // Set default Payment Method for PrePaid payments
            const defaultPaymentMethod = isPrePaidPayment 
              ? PaymentMethods.find(method => 
                  method.key === (paymentTerms === "CreditCard" ? "CreditCard" : "WireTransfer")
                ) || PaymentMethods[0]
              : undefined;
            
            const updatedQuotationInfo = {
              ...quotationInfo,
              taxExpirationDate:res?.data?.data?.taxExpirationDate,
              quotationNumber: quotationInfo.quotationNumber
                ? quotationInfo.quotationNumber
                : "",
              quotationDate: quotationInfo.quotationDate
                ? quotationInfo.quotationDate
                : today.toUTCString(),
              quoteCurrency: customerCountry ? customerCountry.currency : "",
              selectedPaymentTerms: selectedPaymentTerms
                ? selectedPaymentTerms
                : PaymentTerms[1],
              paymentTerms: selectedPaymentTerms
                ? selectedPaymentTerms.key
                : PaymentTerms[1].key,
              paymentType: defaultPaymentType.key,
              selectedPaymentType: defaultPaymentType,
              paymentMethod: defaultPaymentMethod ? defaultPaymentMethod.key : quotationInfo.paymentMethod,
              selectedPaymentMethod: defaultPaymentMethod || quotationInfo.selectedPaymentMethod,
              customerRequestedPromiseDate:
                quotationInfo.customerRequestedPromiseDate
                  ? quotationInfo.customerRequestedPromiseDate
                  : today.toUTCString(),
              customer: {
                ...data,
                selectedBillToAddress: selectedBillToAddress
                  ? selectedBillToAddress
                  : primaryBillToAddress,
                selectedBillToContact: selectedBillToContact
                  ? selectedBillToContact
                  : billTo[0]?.contacts,
                selectedShipToContact: selectedShipToContact
                  ? selectedShipToContact
                  : shipTo[0]?.contacts,
                selectedShipToAddress: selectedShipToAddress
                  ? selectedShipToAddress
                  : primaryShipToAddress,
                customerDiscount: customerDiscount
                  ? customerDiscount
                  : DefaultCustomerDiscounts,
              },
            };
            setQuotationInfo(updatedQuotationInfo);
            if (quotationError.customerAccount) {
              setQuotationError({
                ...quotationError,
                customerAccount: false,
              });
            }
            if(expirationDate && location?.state?.expirationDate){
              checkTaxExpiration(new Date(expirationDate),new Date(location.state?.expirationDate));
            }
            setIsLoading(false);
            setPageLoading(false);
          } else {
            appInsights.trackEvent({
              name: "In AddQuotation.tsx, something went wrong while obtaining customer data.",
              properties: { customerName: "" },
            });
            setQuotationInfo(InitialQuotationInfo);
            setIsLoading(false);
            setPageLoading(false);
          }
        })
        .catch((error) => {
          setQuotationInfo(InitialQuotationInfo);
          appInsights.trackException({
            error,
            properties: {
              name: "In AddQuotation.tsx, error while obtaining customer data by account Id.",
            },
          });
          setIsLoading(false);
          setPageLoading(false);
          setQuotationFailureMessage({
            showMessage: true,
            message: error,
          });
          setIsLoading(false);
        });
    } else {
      appInsights.trackEvent({
        name: "In AddQuotation.tsx, you are trying to search same customer.",
        properties: { customerName: name },
      });
      setPageLoading(false);
    }
  }, [
    location?.state?.customerId,
    location?.state?.accountId,
    location?.state?.quotationId,
  ]);
  const intializeQuotation = () => {
    if (userInfo && userInfo.id === "") {
      fetchLoggedInUser(setUserInfo);
    }
    setQuotationInfo({
      ...quotationInfo,
      quotationDate: today.toUTCString(),
    });
  };
  const getCountry = (address: ICustomerAddress) => {
    getCountriesData(setUser);
    const country = countries?.find(
      (x) =>
        x.countryCode === address?.country ||
        x.countryName.toLocaleLowerCase() ===
          address?.country.toLocaleLowerCase()
    );
    return country;
  };
  useEffect(() => {
    const customerCountry = getCountry(
      quotationInfo.customer.selectedShipToAddress
    );
    setQuotationInfo({
      ...quotationInfo,
      quoteCurrency: customerCountry ? customerCountry.currency : "",
    });
  }, [JSON.stringify(quotationInfo.customer.selectedShipToAddress)]);
  const onChangePaymentType = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
  ) => {
    if (option) {
      if (quotationError.paymentType) {
        const updateShowErrors = {
          ...quotationError,
          paymentType: "",
        };
        setQuotationError(updateShowErrors);
      }
      if (option.key !== PaymentOptions[1].key) {
        setQuotationInfo({
          ...quotationInfo,
          paymentType: option!.key as string,
          selectedPaymentType: option!,
          selectedPaymentMethod: quotationInfo.selectedPaymentMethod
            ? quotationInfo.selectedPaymentMethod
            : PaymentMethods[0],
          paymentMethod: quotationInfo.paymentMethod
            ? quotationInfo.paymentMethod
            : PaymentMethods[0].key,
          customerPurchaseOrder: "",
        });
      } else {
        setQuotationInfo({
          ...quotationInfo,
          paymentType: option!.key as string,
          selectedPaymentType: option!,
          selectedPaymentTerms: quotationInfo.selectedPaymentTerms
            ? quotationInfo.selectedPaymentTerms
            : PaymentTerms[1],
          paymentTerms: quotationInfo.paymentTerms
            ? quotationInfo.paymentTerms
            : PaymentTerms[1].key,
        });
      }
    }
  };
  const onChangePaymentMethod = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
  ) => {
    if (option) {
      if (quotationError.paymentMethod) {
        const updateShowErrors = {
          ...quotationError,
          paymentMethod: "",
        };
        setQuotationError(updateShowErrors);
      }
      setQuotationInfo({
        ...quotationInfo,
        paymentMethod: option!.key as string,
        selectedPaymentMethod: option!,
      });
    }
  };
  const onChangePaymentTerms = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
  ) => {
    if (option) {
      if (quotationError.paymentTerms) {
        const updateShowErrors = {
          ...quotationError,
          paymentTerms: "",
        };
        setQuotationError(updateShowErrors);
      }
      setQuotationInfo({
        ...quotationInfo,
        paymentTerms: option!.key as string,
        selectedPaymentTerms: option!,
      });
    }
  };
  const onChangeDeliveryMode = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
  ) => {
    if (option) {
      if (quotationError.deliveryMode) {
        const updateShowErrors = {
          ...quotationError,
          deliveryMode: "",
        };
        setQuotationError(updateShowErrors);
      }
      setQuotationInfo({
        ...quotationInfo,
        deliveryMode: option!.key as string,
        selectedDeliveryMode: option!,
      });
    }
  };
  const onChangeDeliveryReason = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
  ) => {
    if (option) {
      if (quotationError.deliveryReason) {
        const updateShowErrors = {
          ...quotationError,
          deliveryReason: "",
        };
        setQuotationError(updateShowErrors);
      }
      setQuotationInfo({
        ...quotationInfo,
        deliveryReason: option!.key as string,
        selectedDeliveryReason: option!,
      });
    }
  };
  const getLinksOfAddressesDataGrid = [
    {
      onClick: () => {
        setShowList(true);
      },
      children: <>Select Address/Contact </>,
      underline: true,
    },
  ];
  const _onClickSaveButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setQuotationWarningMessage({
      ...quotationWarningMessage,
      showMessage: false,
    });
    window.scrollTo(0, 0);
    setIsLoading(true);
    appInsights.trackEvent({
      name: "In AddQuotation.tsx, Clicked save button to trigger create quotation API.",
      properties: { quotationNumber: quotationInfo.quotationNumber },
    });
    setQuotationWaitMessage({
      showMessage: true,
      message: `Please wait...`,
    });

    // Set the fill quote session end time using QuoteSessionTracker
    QuoteSessionTracker.setFillQuoteSessionEnd();

    validateQuotationHeaderFields(
      location?.state?.actionName,
      quotationError,
      quotationInfo,
      setIsLoading,
      setQuotationError,
      setQuotationFailureMessage,
      true
    )
      .then((isValid) => {
        if (isValid) {
          onQuotationSave(
            location?.state?.actionName,
            navigate,
            quotationInfo,
            setQuotationInfo,
            setQuotationError,
            setIsLoading,
            setQuotationSuccessMessage,
            setQuotationFailureMessage,
            setQuotationWaitMessage
          );
        } else {          
          if (
            quotationInfo.bundleItems.length === 0 &&
            quotationInfo.standaloneItems.length > 0
          ) {
            try {
              const hasEssentialBundleDiscount =
                quotationInfo.standaloneItems.some((item) =>
                  item.authDiscountAmounts?.some(
                    (discount) =>
                      discount.authorizedDiscountReason ===
                      DefaultEssentialBundleDiscount.contractSelected
                  )
                );
              if (hasEssentialBundleDiscount) {
                setQuotationError({
                  ...quotationError,
                  standaloneItems: true,
                });
                const message =
                  location?.state?.actionName === NEW_ACTION
                    ? `Unable to create`
                    : `Unable to update`;
                setQuotationFailureMessage({
                  showMessage: true,
                  message: `${message} quotation with standalone items only having discount ${DefaultEssentialBundleDiscount.contractSelected}. Please either remove ${DefaultEssentialBundleDiscount.contractSelected} discount or add a bundle to proceed.`,
                });
                setIsLoading(false);
              } else {
                if (quotationError.standaloneItems) {
                  setQuotationError({
                    ...quotationError,
                    standaloneItems: false,
                  });
                  setQuotationFailureMessage({
                    showMessage: true,
                    message: `Please enter correct value in all fields`,
                  });
                  setIsLoading(false);
                }
              }
            } catch (error) {
              setQuotationError({
                ...quotationError,
                standaloneItems: true,
              });
              setQuotationFailureMessage({
                showMessage: true,
                message: error  as string,
              });
              setIsLoading(false);
            }
          } else if (quotationError.standaloneItems) {
            setQuotationError({
              ...quotationError,
              standaloneItems: false,
            });
          }
          setQuotationFailureMessage({
            showMessage: true,
            message: `Please enter correct value in all required fields`,
          });
          setIsLoading(false);
          setQuotationWaitMessage(InitialStateOfResponseMessage);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setQuotationFailureMessage({
          showMessage: true,
          message: error,
        });
        setIsLoading(false);
      });
  };
  const { quotationNumber } = quotationInfo;
  const {
    name,
    taxExempt,
    accountId,
    businessAddress,
    selectedBillToAddress,
    selectedShipToAddress,
    selectedBillToContact,
    selectedShipToContact,
    authorizedBuyerFirstName,
    authorizedBuyerLastName,
    authorizedBuyerPhone,
    authorizedEmailId,
  } = quotationInfo?.customer;
  return (
    <Stack
      style={{
        background: "var(--surface-colors-neutral-lighter-alt, #FAF9F8)",
      }}
    >
      {quotationWarningMessage.showMessage && (
        <MessageBar messageBarType={MessageBarType.warning}>
          {quotationWarningMessage.message}
        </MessageBar>
      )}
      {quotationWaitMessage.showMessage && (
        <Stack horizontal horizontalAlign="start" tokens={stackTokensWithGap20}>
          <MessageBar messageBarType={MessageBarType.warning}>
            <Stack
              horizontal
              horizontalAlign="start"
              tokens={stackTokensWithGap20}
            >
              <Spinner size={SpinnerSize.medium} style={{ paddingRight: 10 }} />
              {quotationWaitMessage.message}
            </Stack>
          </MessageBar>
        </Stack>
      )}
      {quotationSuccessMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() =>
            setQuotationSuccessMessage(InitialStateOfResponseMessage)
          }
        >
          {quotationSuccessMessage.message}
        </MessageBar>
      )}
      {quotationFailureMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() =>
            setQuotationFailureMessage(InitialStateOfResponseMessage)
          }
        >
          {quotationFailureMessage.message}
        </MessageBar>
      )}
      <Breadcrumb
        items={getBreadcrumbItems()}
        style={{ marginLeft: "1%", marginBottom: "1%" }}
        styles={BreadcrumbStyle}
      />
      {pageLoading ? (
        <div style={LoadingContainerStyle}>
          <LoadingState
            primaryText={`Loading ${location?.state?.actionName} Quotation Page`}
            styles={{}}
          />
        </div>
      ) : (
        <Stack verticalFill verticalAlign="start" tokens={stackTokensWithGap20}>
          <Card>
            <Label>{quotationInfo?.quotationNumber}</Label>
            {name && (
              <Label
                style={{
                  fontSize: FontSizes.size18,
                  display: "inline",
                  fontWeight: FontWeights.bold,
                }}
              >
                {name}
              </Label>
            )}
          </Card>
          <Stack style={{ overflowX: "auto" }}>
            <Stack
              style={{ paddingLeft: "80px", width: "900px" }}
              tokens={stackTokensWithGap20}
            >
              <Card
                header={<CustomCardHeader cardTitle="Quotation Details" />}
                styles={cardWidth850}
              >
                <Stack>
                  <Stack horizontal tokens={stackTokensWithGap40}>
                    <StackItem>
                      <DatePicker
                        styles={datePickerStyles}
                        label="Effective Date"
                        value={
                          quotationInfo.quotationDate
                            ? new Date(quotationInfo.quotationDate)
                            : new Date(today)
                        }
                        disabled
                        isRequired
                        strings={defaultDatePickerStrings}
                        formatDate={formatDateForPicker}
                      />
                    </StackItem>
                    <StackItem>
                      <CustomLabel text="Expiration Date" />
                      <DatePicker
                        componentRef={datePickerRef}
                        styles={
                          quotationError.expirationDate
                            ? errorDatePickerStyles
                            : datePickerStyles
                        }
                        ariaLabel="Quotation Details Expiration Date"
                        id="expirationDate"
                        minDate={new Date(today.toUTCString())}
                        aria-errormessage={quotationError.expirationDate}                                                      
                        value={
                          location?.state?.actionName === NEW_ACTION &&
                          !quotationInfo.expiryDate
                            ? new Date(expirationDate)
                            : new Date(quotationInfo.expiryDate)
                        }
                        onSelectDate={(date: Date | null | undefined) => {
                          if(date) {
                            setQuotationInfo({
                              ...quotationInfo,
                              expiryDate: date.toUTCString(),
                            });
                            if(quotationInfo?.taxExpirationDate){
                              checkTaxExpiration(new Date(date),new Date(quotationInfo?.taxExpirationDate))
                            }                            
                          }
                        }}
                        strings={defaultDatePickerStrings}
                        formatDate={formatDateForPicker}
                      />
                      {taxExpireAlertMessage !== "" && (
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: "400",
                            color: "rgb(0, 120, 212)",
                            marginTop: "15px",
                            marginBottom: "5px",
                          }}
                        >
                          {taxExpireAlertMessage}
                        </div>
                      )}
                    </StackItem>
                  </Stack>
                  <Stack horizontal tokens={stackTokensWithGap40}>
                    <StackItem>
                      <TextField
                        aria-label="MSX Opportunity ID"
                        aria-labelledby="msxOpportunityIDLabel"
                        styles={textFieldStyles}
                        label="MSX Opportunity ID"
                        name="msxOpportunityId"
                        autoComplete="off"
                        value={quotationInfo.msxOpportunityId}
                        required
                        onChange={(e, newValue) => {
                          setQuotationInfo({
                            ...quotationInfo,
                            msxOpportunityId: newValue ?? "",
                          });
                          if (
                            newValue &&
                            newValue?.length > 0 &&
                            !allowSpecialCharacterExcludingSome.test(newValue)
                          ) {
                            setQuotationError({
                              ...quotationError,
                              msxOpportunityId:
                                SOME_SPECIAL_CHARACTERS_NOT_ALLOWED,
                            });
                          } else if (
                            newValue && 
                            newValue?.length > 0 && 
                            !msxOpportunityIdRegex.test(newValue)
                          ) {
                            setQuotationError({
                              ...quotationError,
                              msxOpportunityId:
                                MSX_OPPORTUNITY_ID_VALIDATION,
                            });
                          } else if (quotationError.msxOpportunityId) {
                            setQuotationError({
                              ...quotationError,
                              msxOpportunityId: "",
                            });
                          }
                        }}
                        errorMessage={quotationError.msxOpportunityId}
                        onBlur={(e) => {
                          if (
                            quotationInfo.msxOpportunityId.length > 0 &&
                            !allowSpecialCharacterExcludingSome.test(
                              quotationInfo.msxOpportunityId
                            )
                          ) {
                            setQuotationError({
                              ...quotationError,
                              msxOpportunityId:
                                SOME_SPECIAL_CHARACTERS_NOT_ALLOWED,
                            });
                          } else if (
                            quotationInfo.msxOpportunityId.length > 0 &&
                            !msxOpportunityIdRegex.test(
                              quotationInfo.msxOpportunityId
                            )
                          ) {
                            setQuotationError({
                              ...quotationError,
                              msxOpportunityId:
                                MSX_OPPORTUNITY_ID_VALIDATION,
                            });
                          } else if (
                            quotationInfo.msxOpportunityId.length > 0 &&
                            allowSpecialCharacterExcludingSome.test(
                              quotationInfo.msxOpportunityId
                            ) &&
                            msxOpportunityIdRegex.test(
                              quotationInfo.msxOpportunityId
                            )
                          ) {
                            validateMsxOppertunityId(
                              quotationInfo.msxOpportunityId,
                              quotationError,
                              setIsLoading,
                              setQuotationError
                            );
                          }
                        }}
                        deferredValidationTime={200}
                        validateOnFocusIn={true}
                      />
                    </StackItem>
                    <StackItem>
                      <TextField
                        aria-label={`${SHIP_TO} Country`}
                        aria-labelledby="shipToCountryLabel"
                        styles={textFieldGrayStyles}
                        label={`${SHIP_TO} Country`}
                        name="shipToCountry"
                        autoComplete="off"
                        readOnly
                        value={selectedShipToAddress?.country}
                      />
                    </StackItem>
                  </Stack>
                  <Stack horizontal tokens={stackTokensWithGap40}>
                    <StackItem>
                      <TextField
                        aria-label="Quote Currency"
                        aria-labelledby="quoteCurrencyLabel"
                        styles={textFieldGrayStyles}
                        label="Quote Currency"
                        name="quoteCurrency"
                        autoComplete="off"
                        type="currency"
                        readOnly
                        value={quotationInfo.quoteCurrency}
                      />
                    </StackItem>
                  </Stack>
                </Stack>
              </Card>
              {showList && (
                <AddressesAndContactsComponent
                  showList={showList}
                  setShowList={setShowList}
                />
              )}
              {accountId && (
                <Card
                  header={
                    <CustomCardHeader cardTitle="Customer Account Details" />
                  }
                  footer={
                    <CardFooterBase links={getLinksOfAddressesDataGrid} />
                  }
                  styles={cardWidth850}
                >
                  <Stack
                    tokens={stackTokensWithGap30}
                    horizontal
                    horizontalAlign="start"
                  >
                    <Stack
                      verticalAlign="space-between"
                      styles={customerAccountDetailsstyle}
                    >
                      {businessAddress && (
                        <Stack>
                          <StackItem>
                            <h4 style={MarginBlockEndStyle}>Sold To</h4>
                            <p style={GrayText}>{`${businessAddress?.name}`}</p>
                            <p
                              style={GrayText}
                            >{`${businessAddress?.street}`}</p>
                            <p style={GrayText}>
                              {`${businessAddress?.city}, ${businessAddress?.state} ${businessAddress?.postalCode}`}
                            </p>
                            <p style={GrayText}>
                              {`${quotationInfo.customer?.businessAddress?.country}`}
                            </p>
                            <p style={GrayText}>
                              {businessAddress?.addressTaxId}
                            </p>
                          </StackItem>
                        </Stack>
                      )}
                    </Stack>
                    <Stack
                      verticalAlign="space-between"
                      styles={customerAccountDetailsstyle}
                    >
                      {selectedBillToAddress && (
                        <StackItem>
                          <h4 style={MarginBlockEndStyle}>{BILL_TO}</h4>
                          <p
                            style={GrayText}
                          >{`${selectedBillToAddress?.name}`}</p>
                          <p
                            style={GrayText}
                          >{`${selectedBillToAddress?.street}`}</p>
                          <p style={GrayText}>
                            {`${selectedBillToAddress?.city}, ${selectedBillToAddress?.state} ${selectedBillToAddress?.postalCode}`}
                          </p>
                          <p style={GrayText}>
                            {`${selectedBillToAddress?.country}`}
                          </p>
                        </StackItem>
                      )}
                    </Stack>
                    <Stack
                      verticalAlign="space-between"
                      styles={customerAccountDetailsstyle}
                    >
                      {selectedBillToAddress && (
                        <StackItem>
                          <h4 style={MarginBlockEndStyle}>{SHIP_TO}</h4>
                          <p
                            style={GrayText}
                          >{`${selectedShipToAddress?.name}`}</p>
                          <p
                            style={GrayText}
                          >{`${selectedShipToAddress?.street}`}</p>
                          <p style={GrayText}>
                            {`${selectedShipToAddress?.city}, ${selectedShipToAddress?.state} ${selectedShipToAddress?.postalCode}`}
                          </p>
                          <p style={GrayText}>
                            {`${selectedShipToAddress?.country}`}
                          </p>
                        </StackItem>
                      )}
                    </Stack>
                    <Stack verticalAlign="space-between">
                      <StackItem>
                        <h4 style={MarginBlockEndStyle}>Tax Exempt</h4>
                        <p style={GrayText}>{taxExempt ? YES : NO}</p>
                      </StackItem>
                    </Stack>
                  </Stack>
                  <Stack
                    tokens={stackTokensWithGap30}
                    horizontal
                    horizontalAlign="start"
                  >
                    <Stack
                      verticalAlign="space-between"
                      styles={customerAccountDetailsstyle}
                    >
                      {authorizedBuyerFirstName &&
                        authorizedBuyerLastName &&
                        authorizedBuyerPhone &&
                        authorizedEmailId && (
                          <Stack>
                            <StackItem>
                              <h4 style={MarginBlockEndStyle}>
                                Sold To Contact
                              </h4>
                              <p style={GrayText}>
                                {`${authorizedBuyerFirstName} ${authorizedBuyerLastName}`}
                              </p>
                              <p style={GrayText}>{authorizedBuyerPhone}</p>
                              <p style={GrayText}>{authorizedEmailId}</p>
                            </StackItem>
                          </Stack>
                        )}
                    </Stack>
                    <Stack
                      verticalAlign="space-between"
                      styles={customerAccountDetailsstyle}
                    >
                      {selectedBillToContact && (
                        <StackItem>
                          <h4 style={MarginBlockEndStyle}>{BILL_TO} Contact</h4>
                          <p style={GrayText}>
                            {`${selectedBillToContact?.contactFirstName} ${selectedBillToContact?.contactLastName}`}
                          </p>
                          <p style={GrayText}>
                            {selectedBillToContact?.contactPhone}
                          </p>
                          <p style={GrayText}>
                            {selectedBillToContact?.contactEmail}
                          </p>
                        </StackItem>
                      )}
                    </Stack>
                    <Stack
                      verticalAlign="space-between"
                      styles={customerAccountDetailsstyle}
                    >
                      {selectedShipToContact && (
                        <StackItem>
                          <h4 style={MarginBlockEndStyle}>{SHIP_TO} Contact</h4>
                          <p style={GrayText}>
                            {`${selectedShipToContact?.contactFirstName} ${selectedShipToContact?.contactLastName}`}
                          </p>
                          <p style={GrayText}>
                            {selectedShipToContact?.contactPhone}
                          </p>
                          <p style={GrayText}>
                            {selectedShipToContact?.contactEmail}
                          </p>
                        </StackItem>
                      )}
                    </Stack>
                  </Stack>
                </Card>
              )}
              <Card
                header={<CustomCardHeader cardTitle="Payment Details" />}
                styles={cardWidth850}
              >
                <Stack>
                  <Stack horizontal tokens={stackTokensWithGap40}>
                    <StackItem>
                      <ComboBox
                        aria-labelledby="deliveryModeLabel"
                        data-testid="delivery-mode-select"
                        styles={comboBoxStyles}
                        label="Delivery Mode"
                        errorMessage={quotationError.deliveryMode}
                        required
                        autoComplete="off"
                        options={DeliveryOptions}
                        selectedKey={
                          quotationInfo.selectedDeliveryMode
                            ? quotationInfo.selectedDeliveryMode.key
                            : undefined
                        }
                        onChange={onChangeDeliveryMode}
                        allowFreeInput
                      />
                      {/* Hidden label for narration */}
                      <span id="deliveryModeLabel" style={{ display: "none" }}>
                        Payment Details Delivery Mode
                      </span>
                    </StackItem>
                    <StackItem>
                      <ComboBox
                        data-testid="delivery-reason-select"
                        aria-label="Delivery Reason"
                        aria-labelledby="deliveryReasonLabel"
                        styles={comboBoxStyles}
                        label="Delivery Reason"
                        errorMessage={quotationError.deliveryReason}
                        required
                        autoComplete="off"
                        options={DeliveryReasons}
                        selectedKey={
                          quotationInfo.selectedDeliveryReason
                            ? quotationInfo.selectedDeliveryReason.key
                            : undefined
                        }
                        onChange={onChangeDeliveryReason}
                        allowFreeInput
                      />
                    </StackItem>
                  </Stack>
                  <Stack horizontal tokens={stackTokensWithGap40}>
                    <StackItem>
                      <ComboBox
                        data-testid="payment-type-select"
                        aria-label="Payment Type"
                        aria-labelledby="paymentTypeLabel"
                        styles={comboBoxStyles}
                        label="Payment Type"
                        errorMessage={quotationError.paymentType}
                        required
                        autoComplete="off"
                        options={PaymentOptions}
                        selectedKey={
                          quotationInfo.selectedPaymentType
                            ? quotationInfo.selectedPaymentType.key
                            : undefined
                        }
                        onChange={onChangePaymentType}
                        allowFreeInput
                      />
                    </StackItem>
                    <StackItem>
                      <DatePicker
                        componentRef={datePickerRef}
                        styles={datePickerStyles}
                        label="Customer Requested Promise Date"
                        aria-label="Select a date"
                        placeholder="Select a date"
                        minDate={new Date(today)}
                        value={
                          new Date(
                            quotationInfo.customerRequestedPromiseDate
                              ? quotationInfo.customerRequestedPromiseDate
                              : today
                          )
                        }
                        onSelectDate={(date: Date | null | undefined) => {
                          if (date) {
                            setQuotationInfo({
                              ...quotationInfo,
                              customerRequestedPromiseDate: date.toUTCString(),
                            });
                          }
                        }}
                        strings={defaultDatePickerStrings}
                        formatDate={formatDateForPicker}
                      />
                    </StackItem>
                  </Stack>
                  <Stack horizontal tokens={stackTokensWithGap40}>
                    <StackItem>
                      {quotationInfo.selectedPaymentType &&
                        quotationInfo.selectedPaymentType.key ===
                          PaymentOptions[0].key && (
                          <ComboBox
                            aria-label="Payment Method"
                            aria-labelledby="paymentMethodLabel"
                            data-testid="payment-method-select"
                            styles={comboBoxStyles}
                            label="Payment Method"
                            errorMessage={quotationError.paymentMethod}
                            required={
                              quotationInfo.selectedPaymentType &&
                              quotationInfo.selectedPaymentType.key ===
                                PaymentOptions[0].key
                                ? true
                                : false
                            }
                            autoComplete="off"
                            options={PaymentMethods}
                            selectedKey={
                              quotationInfo.selectedPaymentMethod
                                ? quotationInfo.selectedPaymentMethod.key
                                : undefined
                            }
                            onChange={onChangePaymentMethod}
                            allowFreeInput
                          />
                        )}
                      {quotationInfo.paymentType === PaymentOptions[1].key && (
                        <ComboBox
                          data-testid="payment-terms-select"
                          aria-label="Payment Terms"
                          aria-labelledby="paymentTermsLabel"
                          styles={comboBoxStyles}
                          label="Payment Terms"
                          errorMessage={quotationError.paymentTerms}
                          required={
                            quotationInfo.selectedPaymentType &&
                            quotationInfo.selectedPaymentType.key ===
                              PaymentOptions[1].key
                              ? true
                              : false
                          }
                          autoComplete="off"
                          options={PaymentTerms}
                          selectedKey={
                            quotationInfo.selectedPaymentTerms
                              ? quotationInfo.selectedPaymentTerms.key
                              : undefined
                          }
                          onChange={onChangePaymentTerms}
                          allowFreeInput
                        />
                      )}
                    </StackItem>
                    <StackItem>
                      {quotationInfo.selectedPaymentType &&
                        quotationInfo.selectedPaymentType.key && (
                          <TextField
                            aria-label="Customer PO Number"
                            aria-labelledby="customerPurchaseOrderLabel"
                            styles={textFieldStyles}
                            label="Customer PO Number"
                            name="customerPurchaseOrder"
                            autoComplete="off"
                            errorMessage={quotationError.customerPurchaseOrder}
                            value={quotationInfo?.customerPurchaseOrder}
                            onChange={(e, newValue) => {
                              setQuotationInfo({
                                ...quotationInfo,
                                customerPurchaseOrder: newValue ?? "",
                              });
                              if (
                                newValue &&
                                newValue?.length > 0 &&
                                !poNumberRegex?.test(newValue)
                              ) {
                                setQuotationError({
                                  ...quotationError,
                                  customerPurchaseOrder:
                                    CUSTOMER_PURCHASE_ORDER_VALIDATION,
                                });
                              } else if (quotationError.customerPurchaseOrder) {
                                setQuotationError({
                                  ...quotationError,
                                  customerPurchaseOrder: "",
                                });
                              }
                            }}
                          />
                        )}
                    </StackItem>
                  </Stack>
                </Stack>
              </Card>
              <Card
                header={
                  <CustomCardHeader cardTitle="Microsoft Account Details" />
                }
                styles={cardWidth850}
              >
                <Stack>
                  <Stack horizontal tokens={stackTokensWithGap40}>
                    <StackItem>
                      <TextField
                        aria-labelledby="PreparedByLabel"
                        styles={textFieldGrayStyles}
                        label="Prepared By"
                        name="preparedBy"
                        autoComplete="off"
                        value={
                          location?.state?.actionName === NEW_ACTION
                            ? userInfo.displayName
                            : quotationInfo.createdByName
                        }
                        required
                        readOnly
                        errorMessage={quotationError.preparedBy}
                      />
                      <span id="PreparedByLabel" style={{ display: "none" }}>
                        Microsoft Account Details Prepared By
                      </span>
                    </StackItem>
                    <StackItem>
                      <TextField
                        aria-label="Account Lead"
                        aria-labelledby="accountLeadLabel"
                        styles={textFieldStyles}
                        label="Account Lead"
                        name="microsoftAccountLead"
                        autoComplete="off"
                        value={quotationInfo.microsoftAccountLead}
                        onChange={(e, newValue) => {
                          setQuotationInfo({
                            ...quotationInfo,
                            microsoftAccountLead: newValue ?? "",
                          });
                          if (
                            newValue &&
                            newValue?.length > 0 &&
                            !allowSpecialCharacterExcludingSome?.test(newValue)
                          ) {
                            setQuotationError({
                              ...quotationError,
                              microsoftAccountLead:
                                SOME_SPECIAL_CHARACTERS_NOT_ALLOWED,
                            });
                          } else if (quotationError.microsoftAccountLead) {
                            setQuotationError({
                              ...quotationError,
                              microsoftAccountLead: "",
                            });
                          }
                        }}
                        errorMessage={quotationError.microsoftAccountLead}
                      />
                    </StackItem>
                  </Stack>
                  <form autoComplete="off">
                    <Stack horizontal tokens={stackTokensWithGap40}>
                      <StackItem>
                        <TextField
                          aria-label="Account Phone"
                          aria-labelledby="accountPhoneLabel"
                          styles={textFieldStyles}
                          label="Account Phone"
                          name="microsoftAccountPhone"
                          autoComplete="off"
                          value={quotationInfo.microsoftAccountPhone}
                          onChange={(e, newValue) => {
                            setQuotationInfo({
                              ...quotationInfo,
                              microsoftAccountPhone: newValue ?? "",
                            });

                            if (newValue && newValue?.length > 0) {
                              var characterCustomerContactPhone =
                                contactNumberRegex.test(newValue);

                              if (characterCustomerContactPhone) {
                                setQuotationError({
                                  ...quotationError,
                                  microsoftAccountPhone: "",
                                });
                              } else {
                                setQuotationError({
                                  ...quotationError,
                                  microsoftAccountPhone:
                                    SOME_PHONE_SPECIAL_CHARACTERS_NOT_ALLOWED,
                                });
                              }
                            }
                          }}
                          onBlur={(e) => {
                            if (
                              quotationInfo.microsoftAccountPhone &&
                              quotationInfo.microsoftAccountPhone?.length > 0
                            ) {
                              var characterCustomerContactPhone =
                                contactNumberRegex.test(
                                  quotationInfo.microsoftAccountPhone
                                );
                              if (characterCustomerContactPhone) {
                                if (
                                  !phoneNumberRegex.test(
                                    quotationInfo.microsoftAccountPhone
                                  )
                                ) {
                                  setQuotationError({
                                    ...quotationError,
                                    microsoftAccountPhone: INVALID_PHONENUMBER,
                                  });
                                } else {
                                  setQuotationError({
                                    ...quotationError,
                                    microsoftAccountPhone: "",
                                  });
                                }
                              } else {
                                setQuotationError({
                                  ...quotationError,
                                  microsoftAccountPhone:
                                    SPECIL_CHARACTER_INVALID_PHONENUMBER,
                                });
                              }
                            }
                          }}
                          errorMessage={quotationError.microsoftAccountPhone}
                        />
                      </StackItem>
                      <StackItem>
                        <TextField
                          aria-label="Account Email"
                          aria-labelledby="accountEmailLabel"
                          styles={textFieldStyles}
                          label="Account Email"
                          name="microsoftAccountEmail"
                          autoComplete="off"
                          value={quotationInfo.microsoftAccountEmail}
                          errorMessage={quotationError.microsoftAccountEmail}
                          onChange={(e, newValue) => {
                            setQuotationInfo({
                              ...quotationInfo,
                              microsoftAccountEmail: newValue ?? "",
                            });
                            if (
                              newValue &&
                              newValue?.length > 0 &&
                              !allowSpecialCharacterExcludingSome.test(newValue)
                            ) {
                              setQuotationError({
                                ...quotationError,
                                microsoftAccountEmail:
                                  SOME_SPECIAL_CHARACTERS_NOT_ALLOWED,
                              });
                            } else if (quotationError.microsoftAccountEmail) {
                              setQuotationError({
                                ...quotationError,
                                microsoftAccountEmail: "",
                              });
                            }
                          }}
                          onBlur={(e) => {
                            if (
                              quotationInfo.microsoftAccountEmail &&
                              quotationInfo.microsoftAccountEmail?.length >
                                emailCharacterLimit
                            ) {
                              setQuotationError({
                                ...quotationError,
                                microsoftAccountEmail: EMAIL_CHARACTER_LIMIT,
                              });
                            } else if (
                              quotationInfo.microsoftAccountEmail &&
                              quotationInfo.microsoftAccountEmail?.length > 0 &&
                              !allowSpecialCharacterExcludingSome.test(
                                quotationInfo.microsoftAccountEmail
                              )
                            ) {
                              setQuotationError({
                                ...quotationError,
                                microsoftAccountEmail:
                                  SOME_SPECIAL_CHARACTERS_NOT_ALLOWED,
                              });
                            } else if (
                              quotationInfo.microsoftAccountEmail &&
                              quotationInfo.microsoftAccountEmail?.length > 0 &&
                              !emailRegex.test(
                                quotationInfo.microsoftAccountEmail
                              )
                            ) {
                              setQuotationError({
                                ...quotationError,
                                microsoftAccountEmail: EMAIL_VALIDATION,
                              });
                            } else if (quotationError.microsoftAccountEmail) {
                              setQuotationError({
                                ...quotationError,
                                microsoftAccountEmail: "",
                              });
                            }
                          }}
                        />
                      </StackItem>
                    </Stack>
                  </form>
                </Stack>
              </Card>
              <Card
                header={<CustomCardHeader cardTitle="Comments" />}
                styles={cardWidth850}
              >
                <TextField
                  ariaLabel="Required without visible label"
                  aria-labelledby="commentsLabel"
                  maxLength={1500}
                  multiline
                  value={quotationInfo.comments}
                  name="comments"
                  autoComplete="off"
                  placeholder="Please add comments here"
                  errorMessage={quotationError.comments}
                  onChange={(e, newValue) => {
                    setQuotationInfo({
                      ...quotationInfo,
                      comments: newValue ?? "",
                    });
                    if (
                      newValue &&
                      newValue.length > 0 &&
                      !allowSpecialCharacterExcludingSome.test(newValue)
                    ) {
                      setQuotationError({
                        ...quotationError,
                        comments: SOME_SPECIAL_CHARACTERS_NOT_ALLOWED,
                      });
                    } else if (quotationError.comments) {
                      setQuotationError({
                        ...quotationError,
                        comments: "",
                      });
                    }
                  }}
                />
              </Card>
              <Card
                header={<CustomCardHeader cardTitle="Discounts Eligible" />}
                styles={cardWidth850}
              >
                <TextField
                  aria-label="Discounts Eligible"
                  aria-labelledby="discountsEligibleLabel"
                  multiline
                  placeholder="All Eligible Discounts"
                  autoComplete="off"
                  value={quotationInfo.customer?.customerDiscount
                    ?.map((discount) =>
                      discount.minimumDiscountAmount
                        ? `${discount.discountName} (${discount.minimumDiscountAmount}% minimum discount)`
                        : `${discount.discountName}`
                    )
                    .join(", ")}
                  name="discountsEligible"
                  readOnly
                />
              </Card>
              <Stack
                horizontal
                horizontalAlign="center"
                tokens={stackTokensWithGap30}
                styles={cardWidth850}
              >
                <PrimaryButton
                  text="Back"
                  ariaLabel="Back"
                  type="button"
                  disabled={isLoading}
                  styles={btn_width}
                  style={{
                    backgroundColor: isLoading
                      ? "1px solid rgb(0, 120, 212)"
                      : "1px solid lightgrey",
                  }}
                  onClick={() => {
                    setIsLoading(true);
                    resetQuotationForm(
                      quotationInfo,
                      setQuotationInfo,
                      setQuotationError
                    );
                    setIsLoading(false);
                    navigate(-1);
                  }}
                />
                <PrimaryButton
                  text={
                    location?.state?.actionName === NEW_ACTION
                      ? "Create"
                      : "Update"
                  }
                  type="button"
                  ariaLabel={
                    location?.state?.actionName === NEW_ACTION
                      ? "Create"
                      : "Update"
                  }
                  disabled={isLoading}
                  styles={btn_width}
                  style={{
                    backgroundColor: isLoading
                      ? "1px solid rgb(0, 120, 212)"
                      : "1px solid lightgrey",
                  }}
                  onClick={_onClickSaveButton}
                />
                <PrimaryButton
                  text={
                    location?.state?.actionName === NEW_ACTION
                      ? "Add Products"
                      : "Edit/Add Products"
                  }
                  ariaLabel={
                    location?.state?.actionName === NEW_ACTION
                      ? "Add Products"
                      : "Edit/Add Products"
                  }
                  type="button"
                  disabled={isLoading}
                  styles={btn_width}
                  style={{
                    backgroundColor: isLoading
                      ? "1px solid rgb(0, 120, 212)"
                      : "1px solid lightgrey",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => {
                    window.scrollTo(0, 0);
                    setIsLoading(true);
                    
                    // Capture the end time for quote header filling session
                    QuoteSessionTracker.setFillQuoteSessionEnd();
                    
                    appInsights.trackEvent({
                      name: "In AddQuotation.tsx, Clicked Add Line Items button to trigger redirect to Item Details screen.",
                      properties: {
                        quotationNumber: quotationInfo.quotationNumber,
                      },
                    });
                    setQuotationWaitMessage({
                      showMessage: true,
                      message: `Please wait...`,
                    });
                    setQuotationError({
                      ...quotationError,
                      standaloneItems: false,
                    });
                    validateQuotationHeaderFields(
                      location?.state?.actionName,
                      quotationError,
                      quotationInfo,
                      setIsLoading,
                      setQuotationError,
                      setQuotationFailureMessage,
                      false
                    )
                      .then((isValid) => {
                        console.log("expirationDate", quotationError.expirationDate);
                        if(quotationError.expirationDate !== ""){
                            setTaxExpireAlertMessage(quotationError.expirationDate);                            
                        }
                        setQuotationWaitMessage(InitialStateOfResponseMessage);
                        if (isValid) {
                          setIsLoading(false);
                          navigate("/QuotationLineItems", {
                            state: location?.state,
                          });
                        } else {
                          !quotationError.standaloneItems &&
                            setQuotationFailureMessage({
                              showMessage: true,
                              message: `Please enter correct value in all fields`,
                            });
                          setIsLoading(false);
                        }
                      })
                      .catch((error) => {
                        setIsLoading(false);
                        setQuotationWaitMessage(InitialStateOfResponseMessage);
                        setQuotationFailureMessage({
                          showMessage: true,
                          message: error,
                        });
                        setIsLoading(false);
                      });
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
